'use client'

import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { KpiPayload, KpiData } from '../svs-types'
import { sentimentTextClass } from '../color-mapping'

interface KpiRendererProps {
  payload: KpiPayload
}

function TrendIcon({ direction }: { direction: 'up' | 'down' | 'flat' }) {
  switch (direction) {
    case 'up':
      return <TrendingUp className="size-4" />
    case 'down':
      return <TrendingDown className="size-4" />
    case 'flat':
      return <Minus className="size-4" />
  }
}

function Sparkline({ data }: { data: number[] }) {
  if (data.length < 2) return null
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const w = 64
  const h = 24
  const points = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`)
    .join(' ')

  return (
    <svg width={w} height={h} className="text-muted-foreground/40">
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export function KpiCard({ metric, useLabel }: { metric: KpiData; useLabel?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      {useLabel && metric.label && (
        <p className="text-xs font-medium text-muted-foreground">{metric.label}</p>
      )}
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold tracking-tight">{metric.value}</span>
        {metric.sparkline && <Sparkline data={metric.sparkline} />}
      </div>
      {metric.trend && (
        <div className={cn('flex items-center gap-1 text-sm', sentimentTextClass(metric.trend.sentiment))}>
          <TrendIcon direction={metric.trend.direction} />
          <span className="font-medium">{metric.trend.value}</span>
        </div>
      )}
      {metric.context && (
        <p className="text-xs text-muted-foreground">{metric.context}</p>
      )}
    </div>
  )
}

export function KpiRenderer({ payload }: KpiRendererProps) {
  return (
    <KpiCard metric={payload.data} />
  )
}
